import { useState, useEffect, useRef, useCallback } from "react"
import { useLazyQuery } from "@apollo/client"
import { GetInstallationAssetDataDocument, MonitorKind } from "@hydrologiq/gql/graphql"
import { determineRmsStatus, hasRecentIoTData, RmsStatus } from "../utils/rmsStatus"
import { DateTime } from "timezonecomplete"

export interface UseRmsStatusPollingProps {
  assetId: string | undefined
  installationId: string | undefined
  assignmentStatus: "active" | "inactive" | string | null | undefined
  assignmentStart: string | null | undefined
  monitorKind: MonitorKind | string | null | undefined
  isMonitoringActive: boolean
  pollingInterval?: number
}

export interface UseRmsStatusPollingReturn {
  rmsStatus: RmsStatus
  isPolling: boolean
  lastChecked: DateTime | null
}

export function useRmsStatusPolling({
  assetId,
  installationId,
  assignmentStatus,
  assignmentStart,
  monitorKind,
  isMonitoringActive,
  pollingInterval = 30000,
}: UseRmsStatusPollingProps): UseRmsStatusPollingReturn {
  const [rmsStatus, setRmsStatus] = useState<RmsStatus>(null)
  const [isPolling, setIsPolling] = useState(false)
  const [lastChecked, setLastChecked] = useState<DateTime | null>(null)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  const performStatusCheckRef = useRef<(() => Promise<void>) | null>(null)

  const [checkIoTData] = useLazyQuery(GetInstallationAssetDataDocument, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
    notifyOnNetworkStatusChange: false,
    onError: () => {}, // Silently handle errors during polling
  })

  // Determine if we should be polling (memoized)
  const shouldPoll =
    isMonitoringActive &&
    (monitorKind === MonitorKind.Rms || monitorKind === "RMS") &&
    assignmentStatus === "active" &&
    (assetId || installationId) &&
    assignmentStart !== undefined &&
    assignmentStart !== null

  // Memoized status check function
  const performStatusCheck = useCallback(async () => {
    if (!mountedRef.current) return
    if (!assetId && !installationId) return
    if (!assignmentStatus || !assignmentStart) return

    try {
      const result = await checkIoTData({
        variables: {
          installation: installationId || "",
          asset: assetId || "",
          period: "3m",
        },
      })

      if (!mountedRef.current) return

      const hasData = hasRecentIoTData(result.data)

      const status = determineRmsStatus({
        assignmentStatus,
        assignmentStart,
        hasRecentData: hasData,
        monitorKind,
      })

      setRmsStatus(status)
      setLastChecked(new DateTime())
    } catch (error) {
      console.warn("Error checking RMS status:", error)
    }
  }, [assetId, installationId, assignmentStatus, assignmentStart, monitorKind, checkIoTData])

  // Update the ref with the latest function
  performStatusCheckRef.current = performStatusCheck

  // Main effect for managing polling state
  useEffect(() => {
    if (shouldPoll && !isPolling) {
      setIsPolling(true)

      // Check immediately
      performStatusCheckRef.current?.()

      // Set up interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      intervalRef.current = setInterval(() => {
        performStatusCheckRef.current?.()
      }, pollingInterval)
    } else if (!shouldPoll && isPolling) {
      setIsPolling(false)
      setRmsStatus(null)

      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [shouldPoll, isPolling, pollingInterval])

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
        setIsPolling(false)
      }
    }
  }, [])

  return { rmsStatus, isPolling, lastChecked }
}
